import { useEffect, useRef, useState } from 'react';
import NotificationAlert from '../notification';
import Label from '../label';
import { Button, DatePicker, Input } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  UploadOutlined,
  PaperClipOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import {
  AnnualReviewCycle,
  AnnualReviewCycleDetails,
  ClientUtilizationScore,
  ParPointsScore,
  userClientUtilizations,
  userParPoints,
} from '../../types/AnnualReview';
import dayjs from 'dayjs';

import {
  createAnnualReviewCycle,
  getAllAnnualReviewCycles,
  getAnnualCycleById,
  updateAnnualReviewCycle,
} from '../../services/annualServices';
import {
  readUploadFile,
  fileTypes,
  getNextYearDate,
  exportToExcel,
} from './helper';

interface Props {
  editID?: string | null;
  setOpen?(val: boolean): void;
  fetchData?(): void;
}

const defaulData: AnnualReviewCycleDetails = {
  Name: '',
  StartDate: '',
  EndDate: '',
  MentorDeadline: '',
  Year: null,
  ParPointsScoreList: [],
  ClientUtilizationScoreList: [],
  Status: 0,
};

const CreateEditAnnualReview = ({ editID, setOpen, fetchData }: Props) => {
  const myRef = useRef<any>();
  const navigate = useNavigate();
  const [data, setData] = useState<AnnualReviewCycleDetails>({ ...defaulData });
  const [annualData, setAnnualData] = useState<AnnualReviewCycle>();
  const [fillData, setfillData] = useState(false);
  const [isSameYear, setIsSameYear] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isChangedorNull, setIsChangedorNull] = useState(true);
  const parPointsFileInputRef = useRef<HTMLInputElement>(null);
  const clientUtilizationFileInputRef = useRef<HTMLInputElement>(null);
  const [parPointsFileName, setParPointsFileName] = useState<string | null>(
    null
  );
  const [clientUtilizationFileName, setClientUtilizationFileName] = useState<
    string | null
  >(null);

  // handle data Chnages
  // set dates
  const handleChange = (field: string, value: string | string[]) => {
    if (field === 'EndDate' || field === 'StartDate') setIsSameYear(false);

    if (field === 'StartDate') {
      const endDate = getNextYearDate(value as string);
      setData({
        ...data,
        MentorDeadline: '',
        EndDate: endDate,
        Year: new Date(value as string).getFullYear(),
        StartDate: value as string,
      });
    }

    if (field === 'EndDate')
      setData({
        ...data,
        MentorDeadline: '',
        EndDate: value as string,
      });

    if (field !== 'EndDate' && field !== 'StartDate')
      setData({ ...data, [field]: value });
  };

  const checkIsNullOrChanged = () => {
    if (Object.values(data).some((i) => i === '')) {
      setIsChangedorNull(true);
    } else if (
      !editID &&
      data.ParPointsScoreList.length === 0 &&
      data.ClientUtilizationScoreList.length === 0
    ) {
      setIsChangedorNull(true);
    } else if (
      editID &&
      (data.ParPointsScoreList.length !== 0 ||
        data.ClientUtilizationScoreList.length !== 0)
    ) {
      setIsChangedorNull(false);
    } else if (
      data.Name === annualData?.name &&
      data.StartDate === dayjs(annualData?.startDate).format('YYYY-MM-DD') &&
      data.EndDate === dayjs(annualData?.endDate).format('YYYY-MM-DD') &&
      data.MentorDeadline ===
        dayjs(annualData?.mentorDeadline).format('YYYY-MM-DD')
    ) {
      setIsChangedorNull(true);
    } else {
      setIsChangedorNull(false);
    }
  };

  useEffect(() => {
    checkIsNullOrChanged();
  }, [data]);

  useEffect(() => {
    const fetchQuartr = async () => {
      setLoading(true);
      const { data, error } = await getAnnualCycleById(editID as string, 'hr');
      setLoading(false);
      if (error) return myRef.current?.call('error', error?.message);
      setAnnualData(data);
      setData({
        Name: data?.name || '',
        StartDate: dayjs(data?.startDate).format('YYYY-MM-DD'),
        EndDate: dayjs(data?.endDate).format('YYYY-MM-DD'),
        MentorDeadline: dayjs(data?.mentorDeadline).format('YYYY-MM-DD'),
        Year: data?.year || null,
        ParPointsScoreList: [],
        ClientUtilizationScoreList: [],
        Status: data.status,
      });
    };
    if (editID) {
      fetchQuartr();
    }
  }, [editID]);

  const checkValidation = () => {
    let ret = true;
    setfillData(false);
    Object.entries(data).forEach((val) => {
      if (val[1] === '' || (!editID && val[1].length === 0)) ret = false;
    });
    return ret;
  };

  const handleSubmit = async () => {
    if (!checkValidation()) {
      return setfillData(true);
    }
    setLoading(true);

    // Check if the given date range overlaps with any existing annual cycle
    if (!editID) {
      if (await getAnnualQuarterData(data.StartDate, data.EndDate)) {
        setIsSameYear(true);
        setLoading(false);
        return myRef.current?.call('error', 'Annual cycle already exists');
      }
    }

    const { error } =
      editID && annualData
        ? await updateAnnualReviewCycle(annualData.id, {
            Name: data.Name,
            StartDate: data.StartDate,
            EndDate: data.EndDate,
            MentorDeadline: data.MentorDeadline,
            Year: data.Year,
            Status: data.Status,
            ParPointsScoreList: data.ParPointsScoreList,
            ClientUtilizationScoreList: data.ClientUtilizationScoreList,
          } as AnnualReviewCycleDetails)
        : await createAnnualReviewCycle({
            ...data,
            Status: 0,
          });
    if (error) {
      setLoading(false);
      return myRef.current?.call('error', error?.message);
    }
    myRef.current?.call('success');

    setTimeout(() => {
      setLoading(false);
      fetchData?.();
      setOpen?.(false);
    }, 400);

    if (!editID) navigate(-1);
  };

  const getAnnualQuarterData = async (startDate: string, endDate: string) => {
    const { data, error } = await getAllAnnualReviewCycles('hr');
    if (error) return myRef.current?.call('error', error?.message);

    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);

    const annual = data.find((i) => {
      const iStartDate = new Date(i.startDate);
      const iEndDate = new Date(i.endDate);

      // Check if the given date range overlaps with the annual's date range
      return (
        (startDateObj >= iStartDate && startDateObj <= iEndDate) ||
        (endDateObj >= iStartDate && endDateObj <= iEndDate) ||
        (startDateObj <= iStartDate && endDateObj >= iEndDate)
      );
    });

    return annual;
  };

  // handle file upload for PAR Points
  const handleParPointsButtonClick = () => {
    setParPointsFileName(null);
    setIsChangedorNull(true);
    setData({
      ...data,
      ParPointsScoreList: [],
    });
    parPointsFileInputRef.current?.click();
  };

  // handle file upload for Client Utilization
  const handleClientUtilizationButtonClick = () => {
    setClientUtilizationFileName(null);
    setIsChangedorNull(true);
    setData({
      ...data,
      ClientUtilizationScoreList: [],
    });
    clientUtilizationFileInputRef.current?.click();
  };

  // handle file change for PAR Points
  const handleParPointsFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      try {
        const ParPointsScoreList = await readUploadFile(e, fileTypes.parPoints);
        setData({
          ...data,
          ParPointsScoreList: ParPointsScoreList as ParPointsScore[],
        });
        setParPointsFileName(e.target.files[0].name);
      } catch (error) {
        console.error(error);
        myRef.current?.call('error', error);
      }
    }
  };

  // handle file change for Client Utilization
  const handleClientUtilizationFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      try {
        const ClientUtilizationScoreList = await readUploadFile(
          e,
          fileTypes.clientUtilization
        );
        setData({
          ...data,
          ClientUtilizationScoreList:
            ClientUtilizationScoreList as ClientUtilizationScore[],
        });
        setClientUtilizationFileName(e.target.files[0].name);
      } catch (error) {
        console.error(error);
        myRef.current?.call('error', error);
      }
    }
  };

  const handleExportData = (fileType: fileTypes) => {
    exportToExcel(
      fileType === fileTypes.parPoints
        ? (annualData?.userParPoints as userParPoints[])
        : (annualData?.userClientUtilizations as userClientUtilizations[]),
      fileType
    );
  };

  return (
    <div>
      <div className="opacity-45">
        {editID
          ? 'Use this form to update the details of a created annual review cycle. Once a cycle is initiated you can only update the mentor deadline.'
          : 'Use this form to initiate a new annual review cycle by setting the review period dates and a mentor deadline. Once the annual review cycle is created, tailored review forms will be shown to mentors on the front-end based on the tier of each employee.'}
      </div>
      <div className="mt-10 flex flex-col gap-y-10">
        <div className="grid grid-cols-5 gap-x-[10px]">
          <Label label="Cycle Name" className="col-span-5">
            <Input
              disabled={
                annualData?.status === 1 ||
                annualData?.status === 3 ||
                annualData?.status === 2
                  ? true
                  : false
              }
              placeholder="Annual Cycle Name"
              onChange={(e) => handleChange('Name', e.target.value)}
              value={data.Name}
            />
          </Label>
        </div>
        <div className="grid grid-cols-3 gap-x-[10px]">
          <Label label="Start Date" className="">
            <DatePicker
              disabled={
                annualData?.status === 1 ||
                annualData?.status === 3 ||
                annualData?.status === 2
                  ? true
                  : false
              }
              className="w-full"
              placeholder="Select date"
              value={data.StartDate === '' ? null : dayjs(data.StartDate)}
              onChange={(_, dateString) =>
                handleChange('StartDate', dateString)
              }
            />
          </Label>
          <Label label="End Date" className="">
            <DatePicker
              disabled={
                annualData?.status === 1 ||
                annualData?.status === 3 ||
                annualData?.status === 2 ||
                data.StartDate === ''
                  ? true
                  : false
              }
              className="w-full"
              placeholder="Select date"
              value={data.EndDate === '' ? null : dayjs(data.EndDate)}
              onChange={(_, dateString) => handleChange('EndDate', dateString)}
            />
          </Label>
          <Label label="Mentor Deadline" className="">
            <DatePicker
              className="w-full"
              placeholder="Select date"
              value={
                data.MentorDeadline === '' ? null : dayjs(data.MentorDeadline)
              }
              disabled={data.StartDate === '' || data.EndDate === ''}
              // maxDate={dayjs(data.EndDate)}
              minDate={dayjs(data.StartDate)}
              onChange={(_, dateString) =>
                handleChange('MentorDeadline', dateString)
              }
            />
          </Label>
        </div>
      </div>

      <div className="flex justify-start items-start gap-2 mt-10">
        <div className="mr-[10px]">
          <Button
            icon={<UploadOutlined />}
            onClick={handleParPointsButtonClick}
            disabled={data.Status >= 1}
          >
            Upload PAR Points Excel
          </Button>
          <input
            type="file"
            ref={parPointsFileInputRef}
            style={{ display: 'none' }}
            onChange={handleParPointsFileChange}
            accept=".xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          />

          {data.ParPointsScoreList.length > 0
            ? parPointsFileName && (
                <div className="mt-2 flex justify-start items-center gap-2">
                  <PaperClipOutlined />
                  <p className="text-blue-500">{parPointsFileName}</p>
                </div>
              )
            : editID &&
              !loading && (
                <div
                  className=" mt-2 flex justify-start items-center gap-2 cursor-pointer"
                  onClick={() => handleExportData(fileTypes.parPoints)}
                >
                  <DownloadOutlined />
                  <p className="text-blue-500 underline">PAR Points.xlsx</p>
                </div>
              )}
        </div>
        <div className="mr-[10px]">
          <Button
            icon={<UploadOutlined />}
            onClick={handleClientUtilizationButtonClick}
            disabled={data.Status >= 1}
          >
            Upload Client Utilization Excel
          </Button>
          <input
            type="file"
            ref={clientUtilizationFileInputRef}
            style={{ display: 'none' }}
            onChange={handleClientUtilizationFileChange}
            accept=".xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          />

          {data.ClientUtilizationScoreList.length > 0
            ? clientUtilizationFileName && (
                <div className="mt-2 flex justify-start items-center gap-2">
                  <PaperClipOutlined />
                  <p className="text-blue-500">{clientUtilizationFileName}</p>
                </div>
              )
            : editID &&
              !loading && (
                <div
                  className=" mt-2 flex justify-start items-center gap-2 cursor-pointer"
                  onClick={() => handleExportData(fileTypes.clientUtilization)}
                  hidden={loading}
                >
                  <DownloadOutlined />
                  <p className="text-blue-500 underline">
                    Client Utilization.xlsx
                  </p>
                </div>
              )}
        </div>
      </div>
      <div className="flex mt-10">
        {fillData ? (
          <div className="text-[red]">Please fill all the fields</div>
        ) : isSameYear ? (
          <div className="text-[red]">
            Cannot create multiple quarters in same date range.
          </div>
        ) : null}
        <div className="flex gap-x-2 ml-auto">
          <Button
            onClick={() => {
              if (editID) setOpen?.(false);
              else navigate(-1);
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            disabled={loading || isChangedorNull || isSameYear}
          >
            {!editID ? 'Create New Review Cycle' : 'Update Review Cycle'}
          </Button>
        </div>
      </div>
      <NotificationAlert ref={myRef} />
    </div>
  );
};

export default CreateEditAnnualReview;
